import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import State from '../app/store/state';
import { ActionCreators } from './vendors';
import { Input } from '../utils/inputs';
import { hasFeatureAccess } from '../login/auth';
import { ManagerPermission } from '../permission-profiles/permission';
import { InterventionSetIdEnum } from '../constants/intervention-set.enum';

interface VendorBranchesAddProps {
    editMode?: boolean;
    initialValues?: {
        outlet: string;
        interventionSetId: number;
    };
    onCancel?: () => void;
    onSave?: (values: { outlet: string; interventionSetId: number }) => void;
}

export default function VendorBranchesAdd({ editMode, initialValues, onCancel, onSave }: VendorBranchesAddProps) {
    const { vendorId } = useParams<{ vendorId: string }>();
    const [outlet, setOutlet] = useState(initialValues?.outlet || '');
    const [interventionSetId, setInterventionSetId] = useState(
        initialValues?.interventionSetId ?? InterventionSetIdEnum.CoxBazar
    );
    const [outletError, setOutletError] = useState('');
    const auth = useSelector((state: State) => state.auth);
    const dispatch = useDispatch();

    useEffect(() => {
        if (editMode && initialValues) {
            setOutlet(initialValues.outlet);
            setInterventionSetId(initialValues.interventionSetId);
        }
    }, [editMode, initialValues]);

    const submitHandler = () => {
        // Clear previous errors
        setOutletError('');

        // Validate outlet field
        if (outlet.length < 1) {
            setOutletError('Outlet is required');
            return;
        }

        if (editMode && onSave) {
            onSave({ outlet, interventionSetId });
            return;
        }
        ActionCreators.parkBranch(vendorId, outlet, interventionSetId)(dispatch);
    };

    return (
        <div>
            <div className="wfp-form--group row">
                <label className="col-sm-4 ta-right">Outlet</label>
                <div className="col-sm-8">
                    <Input
                        className={`form-control ${outletError ? 'is-invalid' : ''}`}
                        isFocused={true}
                        name="outlet"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setOutlet(event.target.value);
                            if (outletError) {
                                setOutletError('');
                            }
                        }}
                        placeholder="Outlet"
                        type="text"
                        value={outlet}
                    />
                    {outletError && <div className="invalid-feedback d-block">{outletError}</div>}
                </div>
            </div>
            <div className="wfp-form--group row">
                <label className="col-sm-4 ta-right">Location</label>
                <div className="col-sm-8">
                    <select
                        className="form-control"
                        name="interventionSetId"
                        onChange={(event) => setInterventionSetId(Number(event.target.value))}
                        style={{ backgroundImage: 'none' }}
                        value={interventionSetId}
                    >
                        <option value={InterventionSetIdEnum.CoxBazar}>Cox Bazar</option>
                        <option value={InterventionSetIdEnum.BhasanChar}>Bhasan Char</option>
                    </select>
                </div>
            </div>
            {editMode ? (
                <div className="wfp-form--actions text-center">
                    <div className="col-sm-8 col-sm-offset-4">
                        <button className="wfp-btn--primary" onClick={submitHandler} type="button">
                            Save
                        </button>
                        <button className="wfp-btn ml2" onClick={onCancel} type="button">
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                hasFeatureAccess(auth, ManagerPermission.vendorUserPark) && (
                    <div className="wfp-form--actions text-center">
                        <div className="col-sm-8 col-sm-offset-4">
                            <button className="wfp-btn--primary" onClick={submitHandler} type="submit">
                                Park New Branch
                            </button>
                        </div>
                    </div>
                )
            )}
        </div>
    );
}
